<template>
  <header class="global-header">
    <GlobalHeaderPc
      v-if="!isSp"
      :is-logged-in="!isLoading && computedIsLoggedIn"
      :user-name="computedUserName"
      :nav-items="isLoading ? [] : navItemsWithBadge"
      :settings-menu-items="isLoading ? [] : settingsMenuItems"
      :is-active="isActive"
      :on-logout="handleLogout"
      :is-loading="isLoading"
      :platform="computedPlatform"
    />
    <GlobalHeaderSp
      v-else
      :is-logged-in="!isLoading && computedIsLoggedIn"
      :user-name="computedUserName"
      :is-active="isActive"
      :go-search="goSearch"
      :on-logout="handleLogout"
      :is-loading="isLoading"
      :consideration-badge-count="considerationBadgeCount"
      :scout-badge-count="scoutBadgeCount"
      :selection-badge-count="selectionBadgeCount"
      :platform="computedPlatform"
    />
  </header>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import { VIEWPORT_WIDTH_SP_MAX } from '@/constant/scroll';
import router from '@/routers';
import { isClientSideSpaShellPresent } from '@/utils/spaNavigation';
import GlobalHeaderPc from './GlobalHeaderPc.vue';
import GlobalHeaderSp from './GlobalHeaderSp.vue';
import { detectActiveNav, navItems, settingsMenuItems } from './navData';
import type { GlobalHeaderProps, NavItem } from './types.d';

const props = defineProps<GlobalHeaderProps>();

const activeNavLabel = ref<string>('マイページ');
const isSp = ref(false);
const computedIsLoggedIn = ref<boolean>(!!props.isLoggedIn);
const computedUserName = ref<string>(props.userName ?? '');
const computedPlatform = ref<string>('');
const considerationBadgeCount = ref<number | undefined>(props.considerationBadgeCount);
const scoutBadgeCount = ref<number | undefined>(props.scoutBadgeCount);
const selectionBadgeCount = ref<number | undefined>(props.selectionBadgeCount);
const isLoading = ref<boolean>(props.isLoggedIn ? false : true);

const updateIsSp = () => {
  isSp.value = window.innerWidth <= VIEWPORT_WIDTH_SP_MAX;
};

const isActive = (item: NavItem) => activeNavLabel.value === item.label;

const runDetectActive = () => {
  const { pathname, search } = window.location;
  activeNavLabel.value = detectActiveNav(pathname, search);
};

const goSearch = () => {
  if (isClientSideSpaShellPresent()) {
    router.push({ path: '/jobs', query: { agent: 'true' } });
  } else {
    window.location.assign('/jobs?agent=true');
  }
};

const toBadgeCount = (value: unknown): number => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return 0;
  return Math.floor(numeric);
};

const tryFetchProfile = async () => {
  try {
    const response = await fetch('/v1/candidates/profile', { credentials: 'include' });
    if (!response.ok) return;
    const data = await response.json().catch(() => ({}));
    const payload = data?.data || data || {};
    const profile = payload?.candidate || payload?.profile || payload || {};
    if (profile.platform) {
      computedPlatform.value = String(profile.platform);
    }
    considerationBadgeCount.value = toBadgeCount(
      profile.consideration_badge_count ?? profile.considerationBadgeCount
    );
    scoutBadgeCount.value = toBadgeCount(profile.scout_badge_count ?? profile.scoutBadgeCount);
    selectionBadgeCount.value = toBadgeCount(
      profile.selection_badge_count ?? profile.selectionBadgeCount
    );
    const nameCandidate =
      profile.full_name ||
      profile.name ||
      (profile.last_name && profile.first_name ? `${profile.last_name} ${profile.first_name}` : '');
    if (nameCandidate) {
      computedUserName.value = nameCandidate as string;
    }
  } catch (error) {
    console.error('[GlobalHeader] Failed to fetch profile', error);
  } finally {
    isLoading.value = false;
  }
};

const tryFetchMe = async () => {
  try {
    const response = await fetch('/api/me', { credentials: 'include' });
    if (!response.ok) return;
    const data = await response.json().catch(() => ({}));
    if (data?.authenticated === true) {
      computedIsLoggedIn.value = true;
      await tryFetchProfile();
      return;
    }
  } catch (error) {
    console.error('[GlobalHeader] Failed to fetch auth state', error);
  } finally {
    isLoading.value = false;
  }
};

const navItemsWithBadge = computed(() =>
  navItems.map((item) => {
    if (item.label === '気になる/検討中') {
      return { ...item, badgeCount: considerationBadgeCount.value };
    }
    if (item.label === 'スカウト/おすすめ') {
      return { ...item, badgeCount: scoutBadgeCount.value };
    }
    if (item.label === '選考管理') {
      return { ...item, badgeCount: selectionBadgeCount.value };
    }
    return item;
  })
);

const handleLogout = () => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = '/logout';
  const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  if (token) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'authenticity_token';
    input.value = token;
    form.appendChild(input);
  }
  const method = document.createElement('input');
  method.type = 'hidden';
  method.name = '_method';
  method.value = 'delete';
  form.appendChild(method);
  document.body.appendChild(form);
  form.submit();
};

let removeAfterEach: (() => void) | undefined;

onMounted(() => {
  updateIsSp();
  runDetectActive();
  tryFetchMe();
  window.addEventListener('resize', updateIsSp);
  removeAfterEach = router.afterEach(() => {
    runDetectActive();
  });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateIsSp);
  removeAfterEach?.();
});
</script>

<style scoped>
.global-header {
  width: 100%;
}
</style>
